import { z } from 'zod';
import type { CustomFieldType } from '../enums.js';
import { optionalText, paginationQuerySchema, toPatchSchema, uuidSchema } from './common.js';
import {
  customFieldShape,
  withFieldRules,
  type CustomFieldDefinitionSummary,
} from './custom-field.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * Modules an administrator creates at runtime — Zoho's custom objects.
 *
 * The module, its fields and its layout are data. One generic set of endpoints
 * and screens renders any of them, so adding a module is a write rather than a
 * migration and a deploy.
 *
 * A custom module's fields are `CustomFieldDefinition` rows, the same ones that
 * add a field to a standard module. One field-type system, one validator, one
 * admin component — rather than a parallel copy that drifts.
 */

/**
 * The module's key in URLs and payloads.
 *
 * Lowercase and plural because it is a collection in a path: `/custom/projects`.
 * Reserved words are refused so a custom module can never shadow a standard
 * route — a module called `deals` would be unreachable, and which one answered
 * would depend on route registration order.
 */
export const RESERVED_MODULE_NAMES = [
  'leads',
  'accounts',
  'contacts',
  'deals',
  'activities',
  'tasks',
  'calls',
  'meetings',
  'events',
  'notes',
  'attachments',
  'products',
  'vendors',
  'campaigns',
  'cases',
  'solutions',
  'quotes',
  'invoices',
  'sales-orders',
  'salesorders',
  'users',
  'settings',
  'auth',
  'emails',
  'reports',
  'search',
  'custom',
  'related',
  'admin',
  'api',
  'health',
] as const;

export const customModuleApiName = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, 'At least two characters')
  .max(40, 'At most forty characters')
  .regex(/^[a-z][a-z0-9-]*$/u, 'Lowercase letters, digits and dashes; start with a letter')
  .refine(
    (value) => !(RESERVED_MODULE_NAMES as readonly string[]).includes(value),
    'That name belongs to a built-in module',
  );

/**
 * Module Name → URL/API slug
 * ===========================
 * An administrator types one human-readable name — "Customer Projects" — and
 * everything else (the singular label, the URL, the stored-payload key) is
 * derived from it. `apiName` stays a real, independent column: an existing
 * module's slug is never rewritten by this, so nothing that already links to
 * `/custom/projects` breaks when naming conventions change later.
 */

/**
 * Lowercase, hyphenated, ASCII: "Customer Projects" → "customer-projects".
 *
 * Diacritics are folded rather than dropped ("Café" → "cafe") so an accented
 * name still produces a readable slug instead of an empty one. Whatever is
 * left that is not a letter or digit becomes a hyphen, runs of hyphens
 * collapse to one, and the ends are trimmed — the same rules the backend's
 * `customModuleApiName` schema enforces, applied before the value ever
 * reaches it.
 */
export function slugifyModuleName(input: string): string {
  const folded = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/-+/gu, '-')
    .replace(/^-+|-+$/gu, '');

  // The schema requires the slug to start with a letter and be at least two
  // characters — a name that is all digits or symbols ("2026", "!!!") still
  // needs to produce something usable.
  const lettered = /^[a-z]/u.test(folded) ? folded : `m-${folded}`.replace(/^m-$/u, 'module');
  const padded = lettered.length >= 2 ? lettered : `${lettered || 'module'}-module`;

  return padded.slice(0, 40).replace(/-+$/u, '') || 'module';
}

/**
 * The first slug from `base` that is neither reserved nor already taken.
 *
 * Never overwrites an existing module: `customer-projects` stays exactly
 * that, and a second module by the same name becomes `customer-projects-2`,
 * `-3`, and so on. `taken` should include every api name already in use in
 * the organization (a fresh fetch is cheap; a race with another admin is
 * still caught server-side, which refuses a duplicate outright).
 */
export function uniqueModuleSlug(base: string, taken: Iterable<string>): string {
  const used = new Set(Array.from(taken, (name) => name.toLowerCase()));
  const reserved = new Set<string>(RESERVED_MODULE_NAMES);
  const isFree = (candidate: string) => !used.has(candidate) && !reserved.has(candidate);

  if (isFree(base)) return base;

  for (let suffix = 2; suffix < 1000; suffix += 1) {
    // 40 chars total, minus room for "-<suffix>".
    const trimmed = base.slice(0, 40 - String(suffix).length - 1).replace(/-+$/u, '');
    const candidate = `${trimmed}-${suffix}`;
    if (isFree(candidate)) return candidate;
  }

  // Effectively unreachable — a thousand modules sharing one base name —
  // but a deterministic fallback beats an infinite loop.
  return `${base.slice(0, 30)}-${Date.now()}`;
}

/**
 * A best-effort singular form of a plural module name, for the internal
 * `label` field a record's own "New …" button reads. English pluralisation
 * has no general algorithm, so this covers the common regular cases
 * ("Projects" → "Project", "Companies" → "Company", "Boxes" → "Box") and
 * otherwise leaves the name as typed — a user who cares can still edit the
 * singular label from the advanced fields.
 */
export function singularizeLabel(pluralLabel: string): string {
  const trimmed = pluralLabel.trim();
  if (/ies$/iu.test(trimmed)) return trimmed.slice(0, -3) + 'y';
  if (/(sses|shes|ches|xes|zes)$/iu.test(trimmed)) return trimmed.slice(0, -2);
  if (/[^s]s$/iu.test(trimmed)) return trimmed.slice(0, -1);
  return trimmed;
}

/**
 * Per-module presentation and relationship configuration.
 *
 * Stored as one JSON document on the module rather than a column per setting,
 * so a new option is a schema change here and nowhere else. Fields are named by
 * `apiName` (immutable) rather than id, so the config survives a backup/restore
 * into another tenant, where field ids differ.
 */

/**
 * The columns of a standard module a lookup may expose next to the chosen
 * record ("Account phone", "Account GSTIN"). An allow-list, not "any column":
 * what a lookup can show is a decision about what a module's records reveal to
 * whoever can see the custom record, and a column added to a table later must
 * not leak into every module's lookups by accident.
 */
export const LOOKUP_DISPLAY_FIELDS: Record<
  CustomLookupStandardTarget,
  readonly { key: string; label: string }[]
> = {
  ACCOUNT: [
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'website', label: 'Website' },
    { key: 'businessCategory', label: 'Business category' },
    { key: 'gstNumber', label: 'GST number' },
    { key: 'billingCity', label: 'City' },
    { key: 'billingState', label: 'State' },
    { key: 'billingCountry', label: 'Country' },
  ],
  CONTACT: [
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'title', label: 'Job title' },
  ],
  LEAD: [
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'website', label: 'Website' },
    { key: 'status', label: 'Status' },
  ],
  DEAL: [
    { key: 'amount', label: 'Amount' },
    { key: 'status', label: 'Status' },
    { key: 'expectedCloseDate', label: 'Expected close date' },
  ],
  PRODUCT: [
    { key: 'sku', label: 'SKU' },
    { key: 'unitPrice', label: 'Unit price' },
    { key: 'category', label: 'Category' },
  ],
  VENDOR: [
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'website', label: 'Website' },
    { key: 'category', label: 'Category' },
  ],
  CAMPAIGN: [
    { key: 'status', label: 'Status' },
    { key: 'startDate', label: 'Start date' },
    { key: 'endDate', label: 'End date' },
  ],
  CASE: [
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
  ],
};

const fieldKey = z.string().trim().min(1).max(60);

export const customModuleConfigSchema = z.object({
  /** Group records into columns by this PICKLIST field. */
  kanban: z
    .object({
      field: fieldKey,
      /** Fields shown on each card besides the record's name. */
      cardFields: z.array(fieldKey).max(6).default([]),
    })
    .nullish(),
  /** Place records on a calendar by these DATE / DATETIME fields. */
  calendar: z
    .object({
      startField: fieldKey,
      /** Omitted: each record is a single-day entry on its start date. */
      endField: fieldKey.nullish(),
      /** Omitted: the record's name. */
      titleField: fieldKey.nullish(),
    })
    .nullish(),
  /**
   * For each LOOKUP field, which fields of the *target* record to display
   * alongside it. Always live: the value is read from the target on every
   * view, never copied, so the target stays the one source of truth.
   */
  lookupDisplay: z.record(fieldKey, z.array(fieldKey).max(12)).optional(),
});
export type CustomModuleConfig = z.infer<typeof customModuleConfigSchema>;

export const createCustomModuleSchema = z.object({
  apiName: customModuleApiName,
  label: z.string().trim().min(1, 'Required').max(60),
  pluralLabel: z.string().trim().min(1, 'Required').max(60),
  /** A lucide icon name. Unknown names fall back rather than failing. */
  icon: z.string().trim().max(40).optional(),
  description: optionalText(500),
});
export type CreateCustomModuleInput = z.infer<typeof createCustomModuleSchema>;

/** `apiName` is absent on purpose: it is in URLs and stored payload keys. */
export const updateCustomModuleSchema = toPatchSchema(
  z.object({
    label: z.string().trim().min(1, 'Required').max(60),
    pluralLabel: z.string().trim().min(1, 'Required').max(60),
    icon: z.string().trim().max(40).optional(),
    description: optionalText(500),
    isActive: z.boolean(),
    /** Which field titles a record. Must be a field of this module. */
    nameFieldId: uuidSchema.nullish(),
    config: customModuleConfigSchema,
  }),
);
export type UpdateCustomModuleInput = z.infer<typeof updateCustomModuleSchema>;

export interface CustomModuleSummary {
  id: string;
  apiName: string;
  label: string;
  pluralLabel: string;
  icon: string;
  description: string | null;
  isActive: boolean;
  nameFieldId: string | null;
  config: CustomModuleConfig;
  recordCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomModuleDetail extends CustomModuleSummary {
  fields: CustomFieldDefinitionSummary[];
}

// --- Records --------------------------------------------------------------

export const customRecordValues = z.record(z.string(), z.unknown());

export const createCustomRecordSchema = z.object({
  values: customRecordValues.default({}),
  businessManagerId: uuidSchema.optional(),
});
export type CreateCustomRecordInput = z.infer<typeof createCustomRecordSchema>;

export const updateCustomRecordSchema = z.object({
  values: customRecordValues.optional(),
  businessManagerId: uuidSchema.nullish(),
});
export type UpdateCustomRecordInput = z.infer<typeof updateCustomRecordSchema>;

export const listCustomRecordsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(['createdAt', 'updatedAt', 'name']).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  /**
   * A related list: records whose `linkedVia` lookup points at `linkedTo`.
   *
   * Both, not just the id: a module can have several lookups, and "any field
   * happens to hold this id" is neither indexable nor what a related list
   * means — the panel under an account asks for *its* account field.
   */
  linkedTo: uuidSchema.optional(),
  linkedVia: z.string().trim().max(60).optional(),
  /** Records whose `field` equals `value` — one Kanban column's contents. */
  field: z.string().trim().max(60).optional(),
  value: z.string().trim().max(200).optional(),
  /** Records whose `dateField` falls in [from, to] — one calendar page. */
  dateField: z.string().trim().max(60).optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).optional(),
});
export type ListCustomRecordsQuery = z.infer<typeof listCustomRecordsQuerySchema>;

export interface CustomRecordSummary {
  id: string;
  moduleId: string;
  name: string;
  values: Record<string, unknown>;
  businessManager: BusinessManagerRef | null;
  /**
   * What each lookup points at, by field api name. Filled on list responses
   * (resolved in one batched query per lookup field, not per row) so a board
   * or table can name an account without a request per card; an unresolvable
   * target (archived, deleted) is null.
   */
  lookups?: Record<string, RecordRef | null>;
  createdAt: string;
  updatedAt: string;
}

export interface CustomRecordDetail extends CustomRecordSummary {
  /** Resolved lookup targets, keyed by field api name, for display. */
  lookups: Record<string, RecordRef | null>;
  /**
   * Live fields of each lookup's target, as configured in `lookupDisplay`:
   * lookup api name → [{ key, label, value }]. Empty for a lookup with none
   * configured or no target chosen.
   */
  lookupDisplay: Record<string, LookupDisplayValue[]>;
}

export interface LookupDisplayValue {
  key: string;
  label: string;
  value: string | null;
}

/** One related list on a standard (or custom) record's page. */
export interface RelatedCustomList {
  moduleId: string;
  moduleApiName: string;
  label: string;
  pluralLabel: string;
  icon: string;
  /** The lookup on the custom module that points at this record. */
  viaField: string;
  viaFieldLabel: string;
  total: number;
  /** The first few, newest first. */
  records: { id: string; name: string }[];
}

// --- Lookup targets -------------------------------------------------------

/**
 * What a lookup field can point at.
 *
 * A standard module by name, or a custom one as `custom:<id>`. One string
 * rather than two nullable columns, because a lookup has exactly one target
 * and two columns invite the state where it has none or both.
 */
export const CUSTOM_LOOKUP_STANDARD_TARGETS = [
  'LEAD',
  'ACCOUNT',
  'CONTACT',
  'DEAL',
  'PRODUCT',
  'VENDOR',
  'CAMPAIGN',
  'CASE',
] as const;
export type CustomLookupStandardTarget = (typeof CUSTOM_LOOKUP_STANDARD_TARGETS)[number];

export const CUSTOM_MODULE_TARGET_PREFIX = 'custom:';

export function parseLookupTarget(
  target: string | null | undefined,
):
  | { kind: 'standard'; module: CustomLookupStandardTarget }
  | { kind: 'custom'; moduleId: string }
  | null {
  if (!target) return null;

  if (target.startsWith(CUSTOM_MODULE_TARGET_PREFIX)) {
    const moduleId = target.slice(CUSTOM_MODULE_TARGET_PREFIX.length);
    return moduleId ? { kind: 'custom', moduleId } : null;
  }

  return (CUSTOM_LOOKUP_STANDARD_TARGETS as readonly string[]).includes(target)
    ? { kind: 'standard', module: target as CustomLookupStandardTarget }
    : null;
}

export function formatLookupTarget(moduleId: string): string {
  return `${CUSTOM_MODULE_TARGET_PREFIX}${moduleId}`;
}

/** Field types a custom module may use. Everything except nothing, so far. */
export const CUSTOM_MODULE_FIELD_TYPES: readonly CustomFieldType[] = [
  'TEXT',
  'TEXTAREA',
  'NUMBER',
  'CURRENCY',
  'DATE',
  'DATETIME',
  'BOOLEAN',
  'PICKLIST',
  'MULTI_PICKLIST',
  'EMAIL',
  'PHONE',
  'URL',
  'LOOKUP',
];

/**
 * A field on a custom module.
 *
 * The standard field shape without `module` — the module is in the URL — plus
 * the target a lookup points at.
 */
export const createCustomModuleFieldSchema = withFieldRules(
  z.object({
    ...customFieldShape,
    /** Required when `type` is LOOKUP; the API refuses one without it. */
    lookupTarget: z.string().trim().max(80).optional(),
  }),
);
export type CreateCustomModuleFieldInput = z.infer<typeof createCustomModuleFieldSchema>;
